import { formatTime } from "@clipboard-health/date-time";
import { Box, type SxProps, type Theme } from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";

import { ChatMessageTimestamp } from "../ChatMessageTimestamp";
import type { AdminChatMessage } from "../types";
import { formatWorkplaceTimestamp } from "../utils/formatMessageTime";
import { parseCustomMessage } from "../utils/parseCustomMessage";
import { ChatMessageBody } from "./ChatMessageBody";

interface WorkerLockNotificationMessageProps {
  message: AdminChatMessage;
  /** IANA time zone of the workplace, used for the admin "WPL Time" label. */
  facilityTimeZone?: string;
  /**
   * When true (Clipboard admin viewer), the timestamp is shown as an explicit
   * workplace-local "WPL Time: …" label. Workplace customers leave this false.
   */
  isAdmin?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Renders a worker lock notification as an outgoing message bubble with preserved newlines.
 * Visually identical to a regular text message sent from the workplace.
 */
export function WorkerLockNotificationMessage(props: WorkerLockNotificationMessageProps) {
  const { message, facilityTimeZone, isAdmin = false, sx } = props;
  const displayMessage = parseCustomMessage(message.message);
  // NOTE: the non-admin timestamp below uses device time (`formatTime`), unlike
  // the other bubbles which already format in the workplace time zone. That
  // pre-existing inconsistency is left as-is; admins get the labeled WPL time.
  const workplaceTimestamp = isAdmin
    ? formatWorkplaceTimestamp(message.createdAt, facilityTimeZone)
    : undefined;

  return (
    <Box
      sx={mergeSxProps(
        {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        },
        sx
      )}
    >
      <ChatMessageTimestamp
        senderName="Clipboard Health"
        timestamp={formatTime(new Date(message.createdAt))}
        workplaceTimestamp={workplaceTimestamp}
      />
      <Box
        sx={(theme) => ({
          display: "inline-flex",
          flexDirection: "column",
          gap: 1,
          paddingX: 4,
          paddingY: 3,
          borderRadius: theme.borderRadius?.xLarge,
          backgroundColor: theme.palette.background.chat?.outgoing ?? theme.palette.primary.main,
          maxWidth: "80%",
          overflowWrap: "break-word",
          wordBreak: "break-word",
        })}
      >
        <ChatMessageBody text={displayMessage} isIncoming={false} />
      </Box>
    </Box>
  );
}
